"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RequiredPortWithDefault = void 0;
/**
 * SOLID Review
 * S — Strong. One concern: resolve a required interface, falling back to a supplied default when no
 *     provided port has been connected. Connection tracking and resolution are the same
 *     responsibility a socket already has; only the unconnected policy differs.
 * O — Strong. Works for any interface T. The unconnected policy is now a choice between two sibling
 *     classes rather than a fixed property of the kernel, which is the extension point.
 * L — Strong. Substitutable for Port<T>. Note it is deliberately NOT a subtype of RequiredPort<T>:
 *     that class's contract is "getInterface() throws when unconnected", and a subtype that returns
 *     a value instead would violate it. Siblings, not parent and child.
 * I — Strong. Adds connect/isConnected/usingFallback to Port<T>; nothing a consumer must ignore.
 * D — Strong. Depends on the abstract Port<T> and ProvidedPort<T>; the fallback arrives as a
 *     constructor parameter, so this class never names a concrete implementation.
 */
const Port_1 = require("./Port");
const PortContract_1 = require("./PortContract");
/**
 * ⚠️ READ BEFORE USING — this class must not appear in backend runtime wiring or in any test helper.
 *
 * Required port (socket) that degrades to a supplied default instead of throwing.
 *
 * WHY IT EXISTS. §28.21 rejected putting the Port kernel on the frontend, correctly, for one
 * specific reason: `RequiredPort.getInterface()` throws when unconnected — right for a server that
 * must refuse to boot half-wired, wrong for a UI, where the registry's Null-object fallback is what
 * stands between a missing adapter and a white screen. That objection is about THIS KERNEL'S
 * IMPLEMENTATION, not about UML: UML does not specify what an unconnected required port does.
 *
 * So the connection policy becomes a property of the port rather than of the kernel. A component
 * that must fail fast uses `RequiredPort`; a component that must render anyway uses this. Choosing
 * per port carries strictly more information than either mechanism carries alone.
 *
 * WHERE IT MAY BE USED. Frontend composition only — the adapter registry's slots, where Null-object
 * degradation is the established and deliberate behaviour. It is defined here, beside its siblings,
 * because this is where the kernel lives; the location is not permission to use it here.
 *
 * WHERE IT MUST NOT BE USED, and why the distinction is not stylistic:
 *
 *   - **Backend runtime wiring.** A backend that boots half-wired serves wrong answers instead of
 *     refusing to start. The 15 sockets in ComponentApplication stay `RequiredPort`. A silent
 *     fallback there converts a composition-root bug into a data bug discovered later, by someone
 *     else, in production.
 *   - **Any test helper.** §29.4 states it directly: never give test helpers benign defaults that
 *     let an unwired port look wired. A helper that defaults is a helper that cannot fail, and a
 *     suite built on it proves nothing about assembly.
 *
 * HOW THIS IS POLICED, since a prohibition in a comment polices nothing. `describePorts()` reports
 * `defaulted: boolean` for every port it walks, so a port running on its fallback is VISIBLE in the
 * manifest the moment anything enumerates the graph — it is not a silent state. E.6's planned
 * `assertAssembled` is a consumer of exactly that flag rather than a separate mechanism: it fails
 * the boot on any unconnected port absent from an explicit allowlist. The safety net is that flag,
 * and E.6 is where it lands.
 *
 * Nothing consumes this class yet (E.1 is foundation only). It is inert by construction until E.2
 * converts the frontend registry slots into sockets.
 */
class RequiredPortWithDefault extends Port_1.Port {
    fallback;
    connectedPort;
    /**
     * Whether anything has ever resolved this socket — see RequiredPort's note for why this exists.
     *
     * It matters MORE on the defaulting variant: this one never throws, so an unread socket and an
     * unconnected-but-degrading socket are both silent. `usingFallback()` catches the second;
     * only this catches the first.
     */
    read = false;
    /**
     * @param name     the UML port name. It is this port's identity when the graph is ENUMERATED —
     *                 `describePorts`, the socket↔lollipop pairing in `renderComponentDiagram` and
     *                 `ops/generate-system-uml.ts`, and the `assertAssembled`/`assertAllPortsRead`
     *                 allowlists, which qualify it with the component.
     *
     *                 ⚠ AND IT IS MATCHED AT `connect()` TIME SINCE 2026-09-09. This paragraph read
     *                 "It is NOT matched at connect() time … connect() below compares no names at
     *                 all", and drew the correct conclusion from it: a wrong name broke the DIAGRAM
     *                 rather than the wiring, silently becoming a dangling socket plus an unused
     *                 lollipop. That is §28.29's decoy shape, and the honest note about it stood for
     *                 six days before the check it describes was written. (An earlier revision of the
     *                 same line CLAIMED the match happened during assembly while nothing anywhere
     *                 performed one; that was corrected on 2026-09-03, in the other direction.)
     * @param fallback the interface returned while unconnected — a Null object, not a stub that throws
     */
    observer = PortContract_1.NO_OBSERVER;
    guarded;
    guardedOver;
    constructor(name, fallback, contract = null) {
        super(name, contract);
        this.fallback = fallback;
    }
    /**
     * Fill this socket — with the same two refusals as `RequiredPort.connect()`, and they THROW here
     * too, which is worth arguing rather than assuming.
     *
     * This class exists because a UI that throws on a MISSING ADAPTER white-screens where a Null
     * object would have degraded (§28.21, and the outage in §28.33). That argument is about a
     * DEPLOYMENT lacking a registration — an environment condition, invisible until it happens. A name
     * mismatch or a double connect is neither: both are deterministic defects in composition-root
     * code that fire identically on the first render in dev, in every test run, and in production, so
     * degrading gracefully would only hide a bug that cannot reach a user undetected anyway. The
     * frontend's bootstrap already throws on a related condition — `assertAssembled` runs inside it —
     * so this introduces no failure class the boot path did not already have.
     *
     * ⚠ AND THE NAME IS NOW MATCHED AT CONNECT TIME. The note on `name` below said it "is NOT matched
     * at connect() time … connect() below compares no names at all". That was true and is no longer.
     */
    connect(port, observer = PortContract_1.NO_OBSERVER) {
        if (port.name !== this.name) {
            throw new Error(`Required port '${this.name}' cannot be connected to provided port '${port.name}': a socket ` +
                'and the lollipop that fills it must carry the same name, because the generated diagram ' +
                'pairs them by name and nothing else compares them.');
        }
        if (this.connectedPort !== undefined) {
            throw new Error(`Required port '${this.name}' is already connected. A second connect() would discard the ` +
                'first supplier with no trace — wire each socket exactly once, from one composition root.');
        }
        // Same refusal as RequiredPort, for the same reason: a contract mismatch is a composition-root
        // defect, deterministic on the first render, not an environment condition to degrade through.
        const mine = this.contract?.digest ?? null;
        const theirs = port.contract?.digest ?? null;
        if (mine !== theirs) {
            throw new Error(`Required port '${this.name}' cannot be connected: its contract digest is ${mine ?? 'absent'} ` +
                `and the provided port's is ${theirs ?? 'absent'}. Both sides must be built from the same contract.`);
        }
        this.connectedPort = port;
        this.observer = observer;
    }
    isConnected() {
        return this.connectedPort !== undefined;
    }
    /**
     * True while this port is resolving to its fallback rather than to a connected provider.
     *
     * The inverse of isConnected() by construction, and named separately on purpose: callers asking
     * "is this wired?" and callers asking "is this degraded?" are asking different questions of the
     * same fact, and the manifest reports the second.
     */
    usingFallback() {
        return this.connectedPort === undefined;
    }
    /** Whether getInterface() has ever been called on this socket. */
    wasRead() {
        return this.read;
    }
    getInterface() {
        this.read = true;
        const supplier = this.connectedPort?.getInterface() ?? this.fallback;
        if (this.contract === null)
            return supplier;
        // The fallback is guarded on the same terms as a real supplier: a Null object that returns
        // off-contract data is a lie the consumer would otherwise believe. Rebuilt when the supplier
        // changes from fallback to connected, which is the one transition this class allows.
        if (this.guarded === undefined || this.guardedOver !== supplier) {
            this.guardedOver = supplier;
            this.guarded = (0, PortContract_1.guard)(supplier, this.contract, this.observer);
        }
        return this.guarded;
    }
}
exports.RequiredPortWithDefault = RequiredPortWithDefault;
//# sourceMappingURL=PortRequiredWithDefault.js.map